import CloseIcon from '@mui/icons-material/Close';
import { Stack, Typography } from '@mui/material';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  captcha: string | null;
  markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
  getCaptchaLoading: boolean;
  solveCaptchaLoading: boolean;

  handleImageClick(event: React.MouseEvent): void;

  handleDeleteMarker(event: React.MouseEvent): void;

  handleSolveCaptcha(): void;
}

const SolveCaptcha: React.FC<Props> = ({
  captcha,
  markerPosition,
  getCaptchaLoading,
  solveCaptchaLoading,
  handleImageClick,
  handleDeleteMarker,
  handleSolveCaptcha,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const loading = getCaptchaLoading || solveCaptchaLoading;

  const MIN_IMAGE_HEIGHT = 400;
  const [imageHeight, setImageHeight] = React.useState<number>(MIN_IMAGE_HEIGHT);

  const clearMarkerIfAny = () => {
    if (!_.isNil(markerPosition)) {
      try {
        const fakeEvent = {
          preventDefault: () => {},
          stopPropagation: () => {},
        } as unknown as React.MouseEvent;
        handleDeleteMarker(fakeEvent);
      } catch {
        // no-op if the upstream handler requires a DOM event
      }
    }
  };

  const handleZoomIn = () => {
    clearMarkerIfAny();
    setImageHeight((h) => h + 100);
  };

  const handleZoomOut = () => {
    clearMarkerIfAny();
    setImageHeight((h) => Math.max(MIN_IMAGE_HEIGHT, h - 100));
  };

  const zoomPercent = Math.round((imageHeight / MIN_IMAGE_HEIGHT) * 100);
  const isZoomedIn = zoomPercent > 175;
  const MAX_ZOOM_PERCENT = 250;

  return (
    <Stack className={classes.solveCaptcha} gap={3}>
      <Typography className={classes.bigHighlightText} align={'center'}>
        {t('appSyncSolveCaptcha')}
      </Typography>
      <Stack direction={'row'} gap={4}>
        {!isZoomedIn ? (
          <Stack className={classes.instructions} gap={3}>
            <Typography className={classes.greyFormText}>{t('appSyncSolveCaptchaHeadsUp')}</Typography>
            <Stack gap={1}>
              <PaiListItem text={t('appSyncSolveCaptchaInstruction1')} size={'large'} disablePadding={true} />
              <PaiListItem text={t('appSyncSolveCaptchaInstruction2')} size={'large'} disablePadding={true} />
              <PaiListItem text={t('appSyncSolveCaptchaInstruction3')} size={'large'} disablePadding={true} />
              <PaiListItem text={t('appSyncSolveCaptchaInstruction4')} size={'large'} highlight={true} disablePadding={true} />
            </Stack>
          </Stack>
        ) : null}
        {!_.isNil(captcha) ? (
          <Stack gap={1} className={classes.rightColumn} sx={{ marginLeft: isZoomedIn ? 'auto' : undefined }}>
            <div className={classes.zoomBar}>
              <PaiButton text={'−'} border={'rounded'} size={'small'} action={handleZoomOut} disabled={imageHeight <= MIN_IMAGE_HEIGHT} />
              <Typography className={classes.zoomText}>{zoomPercent}%</Typography>
              <PaiButton text={'+'} border={'rounded'} size={'small'} action={handleZoomIn} disabled={zoomPercent >= MAX_ZOOM_PERCENT} />
            </div>
            <div
              className={classes.captchaContainer}
              role={'button'}
              tabIndex={0}
              onClick={!loading ? handleImageClick : undefined}
              onKeyDown={() => {}}
              style={{ cursor: !loading ? 'pointer' : undefined }}
            >
              {loading ? (
                <div className={classes.loaderContainer}>
                  <PaiDotLoader size={'large'} />
                </div>
              ) : null}
              <img className={cx(classes.captcha, loading ? 'loading' : undefined)} src={captcha} alt={'captcha'} style={{ height: imageHeight }} />
              {!_.isNil(markerPosition) && !loading ? (
                <div
                  className={classes.marker}
                  role={'button'}
                  tabIndex={0}
                  style={{ left: `calc(${markerPosition.x}px - 24px)`, top: `calc(${markerPosition.y}px - 24px)` }}
                  onContextMenu={handleDeleteMarker}
                >
                  <CloseIcon className={classes.innerMarker} />
                </div>
              ) : null}
            </div>
          </Stack>
        ) : null}
      </Stack>
      <Stack direction={'row'} justifyContent={'flex-end'}>
        <PaiButton
          text={t('continue')}
          border={'rounded'}
          action={handleSolveCaptcha}
          disabled={_.isNil(markerPosition)}
          loading={solveCaptchaLoading}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  solveCaptcha: {
    padding: '0 0 24px',
    width: '100%',
    [theme.breakpoints.down(1500)]: {
      padding: '0 0 16px',
    },
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
  },
  instructions: {
    flexBasis: '100%',
  },
  bigHighlightText: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  greyFormText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  highlightText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  captchaContainer: {
    position: 'relative',
    width: 'fit-content',
    margin: 0,
  },
  loaderContainer: {
    position: 'absolute',
    zIndex: 999,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  rightColumn: {
    flex: 1,
    alignItems: 'flex-end',
  },
  marker: {
    position: 'absolute',
    width: '48px',
    height: '48px',
    border: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  innerMarker: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.error.main,
    pointerEvents: 'none',
  },
  captcha: {
    height: '300px',
    width: 'auto',
    '&.loading': {
      filter: 'saturate(0%)',
    },
  },
  zoomBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    background: theme.palette.common.white,
    borderRadius: '10px',
    padding: '8px 12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    alignSelf: 'flex-end',
  },
  zoomText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.dark,
    minWidth: '36px',
    textAlign: 'center',
  },
}));

export default SolveCaptcha;
